import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, Users, Search, Edit3, Key, Building2, User as UserIcon, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { CountrySelect, ProvinceSelect } from "@/components/GeoSelect";

const EMPTY = {
  tipo: "privado",
  fullname: "",
  email: "",
  password: "",
  telefono: "",
  telefono_alt: "",
  empresa: "",
  rfc_nit: "",
  documento_id: "",
  sector: "",
  persona_contacto: "",
  direccion: "",
  ciudad: "",
  provincia: "",
  pais: "",
  codigo_postal: "",
  oficina_asignada: "",
  numero_cuenta: "",
  notas: "",
};

export default function Clientes() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [resetTarget, setResetTarget] = useState(null); // {id, name, email}
  const [resetResult, setResetResult] = useState(null); // {mode, new_password, sent_to}

  const load = () => api.get("/clientes").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/oficinas").then((r) => setOficinas(r.data.oficinas || [])).catch(() => {});
    api.get("/countries").then((r) => setCountries((r.data || []).filter((c) => c.activo))).catch(() => {});
     
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCompany = form.tipo === "empresa";

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setShowForm(true);
  };
  const openEdit = (c) => {
    setForm({
      tipo: c.tipo || "privado",
      fullname: c.fullname || "",
      email: c.email || "",
      password: "",
      telefono: c.telefono || "",
      telefono_alt: c.telefono_alt || "",
      empresa: c.empresa || "",
      rfc_nit: c.rfc_nit || "",
      documento_id: c.documento_id || "",
      sector: c.sector || "",
      persona_contacto: c.persona_contacto || "",
      direccion: c.direccion || "",
      ciudad: c.ciudad || "",
      provincia: c.provincia || "",
      pais: c.pais || "",
      codigo_postal: c.codigo_postal || "",
      oficina_asignada: c.oficina_asignada || "",
      numero_cuenta: c.numero_cuenta || "",
      notas: c.notas || "",
    });
    setEditingId(c.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { ...form };
        // Only send password if user actually typed one
        if (!payload.password) delete payload.password;
        delete payload.email; // email non-editable
        await api.patch(`/clientes/${editingId}`, payload);
        toast.success(t("saved"));
      } else {
        await api.post("/clientes", form);
        toast.success(t("saved"));
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const doReset = async (mode, newPassword) => {
    try {
      const { data } = await api.post(`/clientes/${resetTarget.id}/reset-password`, {
        mode,
        new_password: mode === "manual" ? newPassword : undefined,
      });
      setResetResult(data);
      if (mode === "email") {
        toast.success(`${t("password_sent_email")} ${data.sent_to}`);
      } else {
        toast.success(t("password_reset_success"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((c) =>
      [c.fullname, c.email, c.empresa, c.telefono, c.telefono_alt, c.rfc_nit, c.ciudad, c.provincia, c.numero_cuenta, c.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  return (
    <div className="space-y-4" data-testid="clientes-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-red-600" /> {t("clients")}
          </h2>
          <p className="text-sm text-slate-500">
            {filtered.length} / {rows.length} {t("clients").toLowerCase()}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="clients-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_clients")}
              className="ps-9 pe-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 w-72"
            />
          </div>
          <button
            data-testid="new-client-btn"
            onClick={openNew}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition"
          >
            <Plus className="w-4 h-4" /> {t("new_client")}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400">
          <Users className="w-14 h-14 mx-auto opacity-30 mb-3" />
          <p>{rows.length === 0 ? t("no_clients") : t("search_clients")}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("client_type")}</th>
                  <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("name")}</th>
                  <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("email")}</th>
                  <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("phone")}</th>
                  <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("company")}</th>
                  <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("city")}</th>
                  <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("assigned_branch")}</th>
                  {user.role !== "sucursal" && (
                    <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("registered_by")}</th>
                  )}
                  <th className="p-3 text-end text-[11px] uppercase tracking-wider font-semibold">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`client-${c.id}`}>
                    <td className="p-3">
                      {c.tipo === "empresa" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-semibold">
                          <Building2 className="w-3 h-3" /> {t("company")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-semibold">
                          <UserIcon className="w-3 h-3" /> {t("private")}
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-medium text-slate-900">
                      {c.fullname}
                      {c.numero_cuenta && <span className="block text-[10px] font-mono text-indigo-600" data-testid={`client-account-${c.id}`}>{c.numero_cuenta}</span>}
                    </td>
                    <td className="p-3 text-slate-600">{c.email}</td>
                    <td className="p-3 text-slate-600 tabular-nums">{c.telefono || "—"}</td>
                    <td className="p-3 text-slate-600">{c.empresa || "—"}</td>
                    <td className="p-3 text-slate-600">{c.ciudad || "—"}</td>
                    <td className="p-3 text-slate-600">
                      {c.oficina_asignada ? (
                        <span className="text-[10px] bg-fuchsia-100 text-fuchsia-700 px-2 py-0.5 rounded font-semibold">
                          {c.oficina_asignada}
                        </span>
                      ) : "—"}
                    </td>
                    {user.role !== "sucursal" && (
                      <td className="p-3 text-slate-600 text-xs">
                        {c.created_by ? (c.created_by_name || "—") : t("self_registered")}
                      </td>
                    )}
                    <td className="p-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          data-testid={`edit-client-${c.id}`}
                          onClick={() => openEdit(c)}
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition"
                          title={t("edit")}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`reset-pass-${c.id}`}
                          onClick={() => { setResetTarget({ id: c.id, name: c.fullname, email: c.email }); setResetResult(null); }}
                          className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition"
                          title={t("reset_password")}
                        >
                          <Key className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="client-modal">
            <div className="flex justify-between items-center sticky top-0 bg-white pb-2 z-10">
              <h3 className="font-display font-semibold text-lg">
                {editingId ? t("edit_client") : t("new_client")}
              </h3>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}>
                <X className="w-5 h-5 text-slate-400 hover:text-slate-700" />
              </button>
            </div>

            {/* Type toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => set("tipo", "privado")}
                data-testid="cf-type-private"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 font-semibold text-sm transition ${
                  !isCompany ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"
                }`}
              >
                <UserIcon className="w-4 h-4" /> {t("private")}
              </button>
              <button
                type="button"
                onClick={() => set("tipo", "empresa")}
                data-testid="cf-type-company"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 font-semibold text-sm transition ${
                  isCompany ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"
                }`}
              >
                <Building2 className="w-4 h-4" /> {t("company")}
              </button>
            </div>

            {/* Basic info */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{isCompany ? t("company") : t("fullname")}</label>
                <input required data-testid="cf-name" value={form.fullname} onChange={(e) => set("fullname", e.target.value)} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("email")}</label>
                <input
                  required
                  type="email"
                  disabled={!!editingId}
                  data-testid="cf-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={inp + (editingId ? " bg-slate-100 cursor-not-allowed" : "")}
                />
              </div>
              {!editingId && (
                <div className="sm:col-span-2">
                  <label className={lbl}>{t("password")}</label>
                  <input
                    required
                    minLength={6}
                    type="password"
                    data-testid="cf-pass"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    className={inp}
                  />
                </div>
              )}
              {isCompany && (
                <>
                  <div>
                    <label className={lbl}>{t("contact_person")}</label>
                    <input data-testid="cf-contact-person" value={form.persona_contacto} onChange={(e) => set("persona_contacto", e.target.value)} className={inp} />
                  </div>
                  <div>
                    <label className={lbl}>{t("rfc_nit")}</label>
                    <input data-testid="cf-rfc" value={form.rfc_nit} onChange={(e) => set("rfc_nit", e.target.value)} className={inp} />
                  </div>
                  <div>
                    <label className={lbl}>{t("sector")}</label>
                    <input data-testid="cf-sector" value={form.sector} onChange={(e) => set("sector", e.target.value)} className={inp} />
                  </div>
                </>
              )}
              {!isCompany && (
                <div>
                  <label className={lbl}>{t("doc_id")}</label>
                  <input data-testid="cf-doc" value={form.documento_id} onChange={(e) => set("documento_id", e.target.value)} className={inp} />
                </div>
              )}
              <div>
                <label className={lbl}>{t("phone")}</label>
                <input data-testid="cf-phone" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("phone_alt")}</label>
                <input data-testid="cf-phone-alt" value={form.telefono_alt} onChange={(e) => set("telefono_alt", e.target.value)} className={inp} />
              </div>
            </div>

            {/* Address */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={lbl}>{t("address")}</label>
                <input data-testid="cf-address" value={form.direccion} onChange={(e) => set("direccion", e.target.value)} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("city")}</label>
                <input data-testid="cf-city" value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("country")}</label>
                <CountrySelect countries={countries} value={form.pais} onChange={(v) => setForm((f) => ({ ...f, pais: v, provincia: "" }))} className={inp} testid="cf-country" />
              </div>
              <div>
                <label className={lbl}>{t("province")}</label>
                <ProvinceSelect countries={countries} country={form.pais} value={form.provincia} onChange={(v) => set("provincia", v)} className={inp} testid="cf-province" />
              </div>
              <div>
                <label className={lbl}>{t("postal_code")}</label>
                <input data-testid="cf-postal" value={form.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)} className={inp} />
              </div>
            </div>

            {/* Branch assignment */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("assigned_branch")}</label>
                {user.role === "sucursal" ? (
                  <input disabled value={user.oficina || ""} className={inp + " bg-slate-100"} />
                ) : (
                  <select data-testid="cf-oficina" value={form.oficina_asignada} onChange={(e) => set("oficina_asignada", e.target.value)} className={inp}>
                    <option value="">— {t("auto_by_province")} —</option>
                    {oficinas.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className={lbl}>{t("notes")}</label>
                <input data-testid="cf-notes" value={form.notas} onChange={(e) => set("notas", e.target.value)} className={inp} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("account_number")}</label>
                <input
                  data-testid="cf-account"
                  value={form.numero_cuenta}
                  onChange={(e) => set("numero_cuenta", e.target.value)}
                  className={inp}
                  placeholder={editingId ? "" : `${t("auto_account")} (CLI-XXXX)`}
                />
                {!editingId && <p className="text-[11px] text-slate-400 mt-1">{t("auto_account")}: CLI-XXXX</p>}
              </div>
            </div>

            {editingId && (
              <div className="border-t border-slate-200 pt-3">
                <label className={lbl}>{t("new_password")} <span className="text-slate-400 normal-case font-normal">({t("optional")})</span></label>
                <input type="password" minLength={6} data-testid="cf-newpass" value={form.password} onChange={(e) => set("password", e.target.value)} className={inp} placeholder="—" />
              </div>
            )}

            <div className="flex gap-3 pt-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-50 transition">
                {t("cancel")}
              </button>
              <button type="submit" data-testid="cf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm transition">
                {t("save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          result={resetResult}
          onClose={() => { setResetTarget(null); setResetResult(null); }}
          onSubmit={doReset}
          t={t}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({ target, result, onClose, onSubmit, t }) {
  const [mode, setMode] = useState("manual");
  const [pw, setPw] = useState("");
  const [copied, setCopied] = useState(false);

  const copy = (txt) => {
    navigator.clipboard.writeText(txt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6 space-y-4" data-testid="reset-pass-modal">
        <div className="flex justify-between items-center">
          <h3 className="font-display font-semibold text-lg flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-600" /> {t("reset_password")}
          </h3>
          <button type="button" onClick={onClose}>
            <X className="w-5 h-5 text-slate-400 hover:text-slate-700" />
          </button>
        </div>
        <p className="text-sm text-slate-500">
          {target.name} · <span className="tabular-nums">{target.email}</span>
        </p>

        {result ? (
          <div className="space-y-3">
            {result.mode === "manual" ? (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1">{t("generated_password")}</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-lg text-slate-900 bg-white px-3 py-2 rounded border border-emerald-200 tabular-nums" data-testid="reset-pass-value">
                      {result.new_password}
                    </code>
                    <button
                      onClick={() => copy(result.new_password)}
                      className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded transition"
                      title={t("copy")}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <Send className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-sm text-slate-700">{t("password_sent_email")} <strong>{result.sent_to}</strong></div>
              </div>
            )}
            <button onClick={onClose} className="w-full bg-slate-100 hover:bg-slate-200 py-2.5 rounded-lg font-semibold text-sm transition" data-testid="reset-close">
              {t("close")}
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode("manual")}
                data-testid="reset-mode-manual"
                className={`py-2 rounded-lg border-2 font-semibold text-xs transition ${mode === "manual" ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`}
              >
                {t("reset_password_manual")}
              </button>
              <button
                onClick={() => setMode("email")}
                data-testid="reset-mode-email"
                className={`py-2 rounded-lg border-2 font-semibold text-xs transition ${mode === "email" ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`}
              >
                {t("reset_password_email")}
              </button>
            </div>
            {mode === "manual" && (
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">{t("new_password")}</label>
                <input
                  type="text"
                  minLength={6}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-pass-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100"
                  placeholder="min. 6"
                />
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={onClose} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-50 transition">
                {t("cancel")}
              </button>
              <button
                onClick={() => onSubmit(mode, pw)}
                disabled={mode === "manual" && pw.length < 6}
                data-testid="reset-confirm"
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm transition"
              >
                {t("save")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
